import { Swap } from '@velocity-exchange/icons';
import { memo, ReactNode, useMemo } from 'react';
import TradeInput from 'src/components/Inputs/TradeInput';
import { InputSize } from 'src/components/primitives/Input/types';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import useSizeInput from 'src/components/TradeForm/SizeInput/hooks/useSizeInput';
import MarketIcon from 'src/components/Utils/MarketIcon';

interface Props {
	baseSizeStringValue: string;
	quoteSizeStringValue: string;
	formattedMarketSymbol: string;
	maxQuoteAvailable?: number;
	onBaseSizeChange: (value: string) => void;
	onQuoteSizeChange: (value: string) => void;
	onInputSourceChange?: (inputSource: 'base' | 'quote') => void;
	stepSize: number;
	sizeErrorMessage?: ReactNode;
	inputClassName?: string;
	disabled?: boolean;
	size?: InputSize;
	dataTestId?: string;
}

function SizeInput({
	baseSizeStringValue,
	quoteSizeStringValue,
	formattedMarketSymbol,
	maxQuoteAvailable,
	onBaseSizeChange,
	onQuoteSizeChange,
	onInputSourceChange,
	stepSize: stepSizeFromProps,
	sizeErrorMessage,
	inputClassName,
	disabled: disabledFromProps,
	size,
	dataTestId,
}: Props) {
	const {
		id,
		value,
		disabled,
		inputSource,
		swapButtonLabel,
		handleChangeSize,
		handleBlur,
		handleChangeInputSource,
		stepSize,
		showErrorMessage,
		handleFocus,
	} = useSizeInput({
		maxQuoteAvailable,
		baseSizeStringValue,
		quoteSizeStringValue,
		onBaseSizeChange,
		onQuoteSizeChange,
		onInputSourceChange,
		formattedMarketSymbol,
		stepSizeFromProps,
		disabledFromProps,
	});

	const suffix = useMemo(() => {
		return (
			<TextButton size="md" onClick={handleChangeInputSource} rightIcon={Swap}>
				<span className="hidden sm:block">{swapButtonLabel}</span>
				<MarketIcon
					className="sm:hidden"
					marketSymbol={swapButtonLabel}
					sizeClass="size-4"
				/>
			</TextButton>
		);
	}, [handleChangeInputSource, swapButtonLabel]);

	const placeholder = (() => {
		if (inputSource !== 'base' || stepSize === undefined) {
			return;
		}

		const stepSizeDecimals = String(stepSize).split('.')[1]?.length ?? 0;

		return `0${stepSizeDecimals ? '.' + '0'.repeat(stepSizeDecimals) : ''}`;
	})();

	return (
		<TradeInput
			id={id}
			size={size}
			label="Size"
			value={value}
			onChange={handleChangeSize}
			onBlur={handleBlur}
			onFocus={handleFocus}
			stepSize={stepSize}
			disabled={disabled}
			inputClassName={inputClassName}
			suffix={suffix}
			error={showErrorMessage && sizeErrorMessage}
			placeholder={placeholder}
			dataTestId={dataTestId}
		/>
	);
}

export default memo(SizeInput);
